/**
 * decisionService
 *
 * Helpers for auto-recording decisions when significant system events fire.
 * Called from stores/commands after governance-significant transitions complete.
 *
 * Canon: DOCS_V4/00-canon/12-decision-recording-spec.v1.md §3
 *
 * Design rules:
 * - Auto-decisions are BEST-EFFORT: failure to write must not block the triggering transition.
 * - The write is ONE server call, record_auto_decision (migration 766): the
 *   decisions table takes no direct client INSERT/UPDATE/DELETE. The server
 *   binds decided_by to the signed-in person, checks membership, and dedups by
 *   source_event_id (or the same-day source/session/cycle tuple).
 * - No optimistic local state; the decisionsStore handles its own state sync.
 * - The body sent here is a fallback. For criteria_finalized and
 *   session_published the server writes what was decided instead — the
 *   criteria and weights, or the ranking's size, cut line and top items —
 *   read from the session row (migration 841).
 * - A person may say why at the moment (criteria locked): the four-part
 *   rationale rides in the same call and lands on the Decision's rationale
 *   columns (migration 985). Nothing written, nothing sent.
 */

import type { DecisionClass, DecisionRelatedObject } from '../types/prioritization/decisions';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import type { RationaleParts } from './rationaleParts';

// ============================================================================
// Source event types (spec §3 trigger inventory)
// ============================================================================

export type AutoDecisionSource =
  | 'session_published'
  | 'criteria_finalized'
  | 'priority_list_approved'
  | 'proposal_accepted';

// ============================================================================
// Context for building an auto-decision payload
// ============================================================================

export interface AutoDecisionContext {
  /** Which system event triggered this decision record. */
  source: AutoDecisionSource;
  workspaceId: string;
  /** UUID of the user who triggered the event. Informational only: the server
   *  records the signed-in person as the decider (record_auto_decision, mig 766). */
  actorId?: string | null;
  /** ID of the originating audit/governance event row (used for dedup). */
  sourceEventId?: string | null;
  sessionId?: string | null;
  cycleId?: string | null;
  /** Human-readable context for title/body interpolation. */
  sessionName?: string | null;
  cycleName?: string | null;
  /** Number of items affected (for priority list approvals, session rankings). */
  itemCount?: number | null;
  relatedObjects?: DecisionRelatedObject[];
  /** The four-part rationale the person wrote at the moment (985); null when nothing was. */
  rationale?: RationaleParts | null;
}

// ============================================================================
// Internal: build the DB row from context
// ============================================================================

/** The p_fields payload record_auto_decision accepts (migrations 766, 985). */
interface AutoDecisionFields extends RationaleParts {
  title: string;
  body: string | null;
  decision_class: DecisionClass | null;
  impact: 'high' | 'medium' | 'low';
  tags: string[];
  related_objects: DecisionRelatedObject[];
  session_id: string | null;
  cycle_id: string | null;
  source_event_id: string | null;
}

export function buildAutoDecisionFields(ctx: AutoDecisionContext): AutoDecisionFields {
  const base: Omit<AutoDecisionFields, 'title' | 'body' | 'decision_class' | 'impact' | 'tags'> = {
    source_event_id: ctx.sourceEventId ?? null,
    session_id:      ctx.sessionId ?? null,
    cycle_id:        ctx.cycleId ?? null,
    related_objects: ctx.relatedObjects ?? [],
    ...(ctx.rationale ?? {}),
  };

  switch (ctx.source) {
    case 'session_published': {
      const name = ctx.sessionName ? `"${ctx.sessionName}"` : 'Session';
      const items = ctx.itemCount != null ? ` (${ctx.itemCount} items ranked)` : '';
      return {
        ...base,
        title:          `${name}: ranking published`,
        body:           `Session ${name} published${items}. Results are now available for downstream use.`,
        decision_class: 'force_ranking',
        impact:         'high',
        tags:           ['session', 'ranking'],
      };
    }

    case 'criteria_finalized': {
      const name = ctx.sessionName ? `"${ctx.sessionName}"` : 'Session';
      return {
        ...base,
        title:          `${name}: criteria finalized`,
        body:           `Criteria selection for session ${name} locked. Participants may now score items.`,
        decision_class: 'selection',
        impact:         'medium',
        tags:           ['session', 'criteria'],
      };
    }

    case 'priority_list_approved': {
      const items = ctx.itemCount != null ? ` (${ctx.itemCount} items)` : '';
      const cycle = ctx.cycleName ? ` for cycle "${ctx.cycleName}"` : '';
      return {
        ...base,
        title:          `Priority list approved${items}`,
        body:           `Priority list approved${cycle}${items}. Items added to the active priority list.`,
        decision_class: 'force_ranking',
        impact:         'high',
        tags:           ['priority-list', 'approval'],
      };
    }

    case 'proposal_accepted': {
      return {
        ...base,
        title:          'Catchball proposal accepted',
        body:           'Catchball proposal accepted. Convergence reached between tier participants.',
        decision_class: 'convergence',
        impact:         'medium',
        tags:           ['catchball', 'proposal'],
      };
    }
  }
}

// ============================================================================
// Public API: autoRecordDecision
// ============================================================================

/**
 * Records an auto decision from a system event.
 * BEST-EFFORT: catches all errors and returns `null` on failure.
 * Callers must not await the result for blocking behavior.
 *
 * @returns The inserted decision id, or null on failure/skip.
 */
export async function autoRecordDecision(ctx: AutoDecisionContext): Promise<string | null> {
  try {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const workspaceId = ctx.workspaceId || getCurrentWorkspaceId();
    if (!workspaceId) return null;

    // One governed call (migration 766). decided_by is the signed-in person
    // server-side; a repeat of the same source event (or the same-day
    // source/session/cycle tuple) returns the existing row as a duplicate.
    const { data, error } = await supabase.rpc('record_auto_decision', {
      p_workspace_id: workspaceId,
      p_source: ctx.source,
      p_fields: buildAutoDecisionFields({ ...ctx, workspaceId }),
    });

    if (error) {
      console.warn('[decisionService] auto-record failed:', error.message);
      return null;
    }
    const result = data as { success?: boolean; decision_id?: string; duplicate?: boolean; error?: string } | null;
    if (!result?.success || !result.decision_id) {
      if (result?.error) console.warn('[decisionService] auto-record refused:', result.error);
      return null;
    }
    if (result.duplicate) return null;

    // Refresh the decisions store if it's loaded so the page updates live
    try {
      const { useDecisionsStore } = await import('../stores/prioritization/decisionsStore');
      const store = useDecisionsStore.getState();
      if (store.decisions.length > 0) {
        // Only refresh if the store has been loaded already (avoid spurious fetches)
        store.fetchDecisions();
      }
    } catch {
      // Non-critical — the store will refresh on next navigation
    }

    return result.decision_id;
  } catch (err) {
    console.warn('[decisionService] auto-record error:', err);
    return null;
  }
}
